<template>
	<section>
		<button @click="$refs.uploadInput.click()">
			<FolderPlusIcon v-if="empty(value) && this.pendingFiles.length === 0"/>
			<CheckIcon v-if="!empty(value) || this.pendingFiles.length > 0"/>
			{{ title }}
		</button>
		<input
			v-if="allowedToUpload && !disabled"
			class="upload-input"
			type="file"
			:multiple="multiple"
			@change="addFiles($event.target.files)"
			ref="uploadInput"
			:accept="accept"
		>
	</section>
</template>

<script>
import {QueuedFile} from "@/common/queued-file";
import LoadingIndicator from "@/components/LoadingIndicator";
import {CheckIcon, FolderPlusIcon, PlusIcon, XIcon} from "vue-feather-icons";
import {mapActions, mapGetters} from "vuex";

export default {
	components: {
		LoadingIndicator,
		FolderPlusIcon,
		CheckIcon
	},
	data() {
		return {
			pendingFiles: [],
			files: [],
			allowedToUpload: false,
			fileName: ''
		};
	},

	props: {
		title: {
			type: String,
			default: ""
		},
		type: {
			type: String,
			default: ""
		},
		companyId: {
			default: ""
		},
		multiple: {
			type: Boolean,
			default: true
		},
		accept: {
			type: String,
			default: "*/*"
		},
		disabled: {
			type: Boolean,
			default: false
		},
		startUpload: {
			type: Boolean,
			default: false
		},
		value: {
			type: String | Number,
			default: ""
		}
	},

	created() {
		this.getFiles();
		this.allowedToUpload = this.allowedTo("files/add");
	},

	watch: {
		startUpload: function (newValue) {
			if (newValue) {
				this.uploadFiles();
			}
		}
	},

	methods: {
		...mapActions({
			fetchFiles: "files/fetchFiles"
		}),
		...mapGetters({
			allowedTo: 'auth/allowedTo',
		}),

		getFiles() {
			this.fetchFiles({
				type: this.type,
				company_id: this.companyId
			}).then(response => {
				this.files = response;
			});
		},

		addFiles(files) {
			if (this.disabled || this.allowedToUpload === false) {
				return;
			}
			for (const file of files) {
				this.pendingFiles.push(new QueuedFile(file, this.type, this.companyId, this.$forceUpdate));
			}
			this.$refs.uploadInput.value = "";
		},

		empty(value) {
			return value == null || (typeof value === "string" && value.trim() === "");
		},

		uploadFiles() {
			return new Promise((resolve, reject) => {
				if (this.disabled) {
					resolve();
					return;
				}

				if (this.pendingFiles.length === 0) {
					resolve();
					return;
				}
				Promise.all(this.pendingFiles.map(file => file.upload())).finally(() => {
					if (this.pendingFiles.length === 1) {
						this.fileName = this.pendingFiles[0].file_name;
					}
					this.pendingFiles = [];
					this.fetchFiles({
						type: this.type,
						company_id: this.companyId
					}).then(response => {
						this.files = response;
						if (this.files.length > 0 && this.empty(this.value)) {
							if (this.multiple === true) {
								this.$emit("input", this.type);
							} else {
								const file = this.files.find(file => file.file_name === this.fileName.replaceAll(' ', '_'));
								if (file != null) {
									this.$emit("input", file.id);
								}
							}
						}
						resolve();
					}).catch((error) => {
						reject(error);
					});
				}).catch(error => {
					reject(error);
				});
			});
		}
	}
};
</script>

<style scoped>

button {
	height: 36px;
	border: 2px solid #35a5a4;
	border-radius: 5px;
	color: #35a5a4;
	background: #fff;
	align-items: center;
	display: flex;
	min-width: 130px;
	justify-content: center;
	gap: 8px;
}

.upload-input {
	visibility: hidden;
	display: none;
}

</style>
